//checkpoint
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, ViewerRelation, Impact, Issue } from 'util/constants';
import { useUser } from 'util/UserContext';
import ActorAvatar from 'components/Actors/ActorAvatar';
import ActorName from 'components/Actors/ActorName';
import { ACTOR_AVATAR_SIZES } from 'util/actorAvatarSizes';
import { toActorFromUser } from 'util/actorIdentity';
import DetailPanel from 'ui/DetailPanel';
import api from 'config/axiosConfig';
import EntityAvatar from '../Entities/EntityAvatar';
import FollowButton from 'components/Common/FollowButton';
import FollowersModal from 'components/Common/FollowersModal';
import AlignmentTile from 'components/Common/AlignmentTile';
import { 
    Carousel, 
    CarouselContent, 
    CarouselItem, 
    CarouselNext, 
    CarouselPrevious 
} from 'ui/Carousel';
import { PrivacyLevel, getUserRelationsByUserId, IssueRanking } from 'util/userUtils';
import { canViewUserFollow } from 'util/getObjectDetails';
import UserAlignmentModal from 'components/User/UserAlignmentModal';
import ScrollableTabsList from 'ui/ScrollableTabsList';
import UserBackgroundTile from './UserBackgroundTile';
import UserPrinciplesSection from './UserPrinciplesSection';
import { getTaskUiStrings, resolveTaskLocale } from 'i18n/taskUi';
import { Award, Clapperboard, Loader2, Smile, Frown, MoreHorizontal, Megaphone, Ribbon } from 'lucide-react';
import AlignmentSummaryDisplay from 'components/Alignment/AlignmentSummaryDisplay';
import { getEntityDetails } from 'util/getObjectDetails';
import UserActivityCarousel from 'components/User/UserActivityCarousel';
import EntitySentiment from 'components/Alignment/EntitySentiment';
import { apiRoutes } from "util/apiRoutes";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";
import { buildUserStoriesPath, useUserStoryStatus } from "components/Stories/useUserStoryStatus";
import {
    useAwardGivingBudgetQuery,
    usePromotionCreditBalanceQuery,
    useReputationQuery,
} from "hooks/queries/useAwardBalanceQuery";
import MenuOrDrawer, { type MenuOrDrawerItem } from "ui/MenuOrDrawer";
import PromoteEntityModal from "components/Promotions/PromoteEntityModal";
import ContentAwardButton from "components/Common/ContentAwardButton";

// Define statement interface
interface AlignmentStatement {
    id: number;
    statement: string;
    dimension: string; // Changed from category to dimension, removed reverse
}

interface SocialLink {
    type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit';
    label: string;
    url: string;
}

interface PropertiesPublic {
    avatar_url: string;
    avatar_key: string;
    about_bio_links?: SocialLink[];
    about_bio_brief?: string;
    about_alignment?: Record<string, string>;
    is_private?: boolean;
}

// Omit the original properties_public and extend with our new one
interface UserWithPublicProps extends Omit<User, 'properties_public'> {
    properties_public: PropertiesPublic;
    about: any;
    viewer_relation?: string;
    post_count?: number;
    follower_count?: number;
    following_count?: number;
}

interface UserProfilePlateProps {
    user: UserWithPublicProps;
    isDetailPanelOpen?: boolean;
    onOpenPanelChange?: (open: boolean) => void;
}

const UserProfilePlate: React.FC<UserProfilePlateProps> = ({ user, isDetailPanelOpen, onOpenPanelChange = () => {} }) => {
    const { currentUser, setShowAuthModal, awaitingUser } = useUser();
    const navigate = useNavigate();
    const isOwnProfile = currentUser?.username === user.username;
    const storyUi = getStoryUiStrings(resolveStoryLocale());
    const [profileUser, setProfileUser] = useState<UserWithPublicProps | null>(user);
    const [issueRankings, setIssueRankings] = useState<IssueRanking[]>([]);
    const [isLoadingRankings, setIsLoadingRankings] = useState<boolean>(false);
    const [followerCount, setFollowerCount] = useState<number>(0);
    const [followingCount, setFollowingCount] = useState<number>(0);
    const [postCount, setPostCount] = useState<number>(0);
    const [canViewFollow, setCanViewFollow] = useState<boolean>(false);
    const IMG_CDN = process.env.REACT_APP_IMAGE_CDN
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);
    
    // State for alignments
    const [alignments, setAlignments] = useState<{ profileUser: any[], currentUser: any[] }>({ profileUser: [], currentUser: [] });
    const [isLoadingAlignments, setIsLoadingAlignments] = useState<boolean>(true);
    const [alignmentsError, setAlignmentsError] = useState<string | null>(null);
    
    const [showAlignmentModal, setShowAlignmentModal] = useState(false);
    const [statementNumber, setStatementNumber] = useState<number | null>(null);
    const [selectedProfileTab, setSelectedProfileTab] = useState<string>('background');
    const [carouselApi, setCarouselApi] = useState<any>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const storyStatusQuery = useUserStoryStatus(user.id, { enabled: Boolean(user?.id) });
    const hasStoryPlacements = storyStatusQuery.data ?? false;
    const awardBudgetQuery = useAwardGivingBudgetQuery(isOwnProfile && Boolean(currentUser));
    const promotionCreditQuery = usePromotionCreditBalanceQuery(isOwnProfile && Boolean(currentUser));
    const reputationQuery = useReputationQuery("user", user.id, Boolean(user?.id));
    const awardBudget = awardBudgetQuery.data?.balance ?? 0;
    const promotionCredits = promotionCreditQuery.data?.balance ?? 0;
    const reputationReceived = reputationQuery.data?.awards_received_count ?? 0;

    // Promote-on-ad-breaks menu + modal state. Menu shows only for the
    // profile owner; eligibility is gated on a non-private account so we
    // do not confuse users with a control that the backend will always
    // reject at submit time.
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [promoteModalOpen, setPromoteModalOpen] = useState(false);
    const profileIsPrivate = user.properties_public?.is_private === true;
    const canPromoteProfile = isOwnProfile && Boolean(currentUser) && !profileIsPrivate;
    const awardBudgetTitle = storyUi.awardGivingBudgetTitle.replace("{count}", String(awardBudget));
    const promotionCreditsTitle = storyUi.promotionCreditsTitle.replace("{count}", String(promotionCredits));
    const reputationTitle = storyUi.reputationReceivedTitle.replace("{count}", String(reputationReceived));

    // Track profile user's sentiments for issues to color rank badges
    type SentimentValue = -1 | 0 | 1;
    const [profileIssueSentiments, setProfileIssueSentiments] = useState<Record<number, SentimentValue>>({});

    useEffect(() => {
        const fetchAlignments = async () => {
            if (!user?.id) return;
            setIsLoadingAlignments(true);
            try {
                // Fetch alignments for the profile user
                const profileUserAlignmentsRes = await api.get(apiRoutes.user.userAlignments(user.id));
                const profileUserRelations = profileUserAlignmentsRes.data.relations || [];

                // Fetch alignments for the current user (if they exist)
                let currentUserRelations = [];
                if (currentUser && currentUser.id !== user.id) {
                    const currentUserAlignmentsRes = await api.get(apiRoutes.user.userAlignments(currentUser.id));
                    currentUserRelations = currentUserAlignmentsRes.data.relations || [];
                } else if (currentUser && currentUser.id === user.id) {
                    currentUserRelations = profileUserRelations;
                }

                const allRelationIds = [...profileUserRelations, ...currentUserRelations].map(r => r.rel_node_id);
                const uniqueTeabateIds: number[] = Array.from(new Set(allRelationIds));

                if (uniqueTeabateIds.length === 0) {
                    setIsLoadingAlignments(false);
                    return;
                }

                const teabateDetailsPromises = uniqueTeabateIds.map((id: number) => 
                    api.get(apiRoutes.posts.byId(id)).catch(e => {
                        console.error(`Failed to fetch teabate ${id}`, e);
                        return null;
                    })
                );
                const teabateDetailsResponses = await Promise.all(teabateDetailsPromises);
                
                const teabateDetailsMap = new Map();
                teabateDetailsResponses.forEach(res => {
                    if (res && res.data) {
                        teabateDetailsMap.set(res.data.id, res.data);
                    }
                });
                
                const combinedData = {
                    profileUser: profileUserRelations.map((r: any) => ({
                        relation: r,
                        teabate: teabateDetailsMap.get(r.rel_node_id)
                    })).filter((item: any) => item.teabate && item.teabate.properties.dimension && item.teabate.properties.dimension !== 'none'),
                    currentUser: currentUserRelations.map((r: any) => ({
                        relation: r,
                        teabate: teabateDetailsMap.get(r.rel_node_id)
                    })).filter((item: any) => item.teabate && item.teabate.properties.dimension && item.teabate.properties.dimension !== 'none')
                };
                
                setAlignments(combinedData);
                if (combinedData.profileUser.length > 0) {
                    setSelectedProfileTab('perspective');
                    setIsPanelOpen(true);
                }

            } catch (error) {
                console.error('Error fetching alignments:', error);
                setAlignmentsError('Failed to load alignments.');
            } finally {
                setIsLoadingAlignments(false);
            }
        };

        fetchAlignments();
    }, [user, currentUser]);

    // Derived alignment statements from API data
    const alignmentStatements: AlignmentStatement[] = React.useMemo(() => {
        if (!alignments.profileUser) return [];

        return alignments.profileUser.map(({ teabate }: { teabate: Impact }) => ({
            id: teabate.id,
            statement: teabate.title,
            dimension: teabate.properties.dimension, // Changed from category to dimension
        }));
    }, [alignments.profileUser]);
    
    // Fetch issue rankings
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await api.get<UserWithPublicProps>(apiRoutes.user.byUsername(user.username));
                setProfileUser(response.data);
                
                // Load issue rankings for the user
                setIsLoadingRankings(true);
                try {
                    const rankingsResponse = await getUserRelationsByUserId(user.id, {
                        relTypes: ['issue_ranking'],
                        relNodeTypes: ['issue'],
                        limit: 100,
                        sortBy: 'properties',
                        sortOrder: 'ASC'
                    });
                    if (rankingsResponse) {
                        // Enrich each ranking with complete issue data
                        const enrichedRankings = await Promise.all(
                            rankingsResponse.relations.map(async (relation: any) => {
                                try {
                                    const fullIssueData = await getEntityDetails('issue', relation.rel_node_id);
                                    return {
                                        id: relation.id,
                                        userId: relation.user_id,
                                        issueId: relation.rel_node_id,
                                        ranking: relation.properties?.value || 1,
                                        privacy: relation.properties?.privacy || 'public',
                                        createdAt: relation.created_at,
                                        updatedAt: relation.updated_at,
                                        issue: fullIssueData
                                    } as IssueRanking;
                                } catch (error) {
                                    console.error(`Error fetching issue ${relation.rel_node_id}:`, error);
                                    return null;
                                }
                            })
                        );
                        // Filter out failed fetches and sort by ranking
                        const validRankings = enrichedRankings
                            .filter((ranking): ranking is IssueRanking => ranking !== null)
                            .sort((a, b) => a.ranking - b.ranking);
                        
                        setIssueRankings(validRankings);

                        // Fetch profile user's sentiments for these issues to color rank badges
                        try {
                            const issueIds: number[] = validRankings
                                .map(r => r.issueId)
                                .filter((id: unknown): id is number => typeof id === 'number');
                            if (issueIds.length > 0) {
                                const sentimentsRes = await getUserRelationsByUserId(user.id, {
                                    relTypes: ['sentiment'],
                                    relNodeTypes: ['issue'],
                                    relNodeIds: issueIds,
                                    limit: 200
                                });
                                const map: Record<number, SentimentValue> = {};
                                if (sentimentsRes && Array.isArray(sentimentsRes.relations)) {
                                    sentimentsRes.relations.forEach((rel: any) => {
                                        const id = typeof rel?.rel_node_id === 'number' ? rel.rel_node_id : null;
                                        const val = rel?.properties?.value_int;
                                        if (id !== null && (val === -1 || val === 0 || val === 1)) {
                                            map[id] = val as SentimentValue;
                                        }
                                    });
                                }
                                setProfileIssueSentiments(map);
                            } else {
                                setProfileIssueSentiments({});
                            }
                        } catch (e) {
                            console.error('Error loading sentiments for profile user issues:', e);
                            setProfileIssueSentiments({});
                        }
                    }
                } catch (error) {
                    console.error('Error loading issue rankings:', error);
                } finally {
                    setIsLoadingRankings(false);
                }
                
                if (response.data.about && typeof response.data.about === 'object' 
                    && Object.keys(response.data.about).some(key => key.startsWith('about_alignment_answer'))) {
                    setSelectedProfileTab('perspective');
                    setIsPanelOpen(true);
                }
                const followerCount = await api.get<{ count: number }>(apiRoutes.follow.followersCount("user", user.id));
                setFollowerCount(followerCount.data.count);
                const followingCount = await api.get<{ count: number }>(apiRoutes.follow.followingCount("user", user.id));
                setFollowingCount(followingCount.data.count);
                setCanViewFollow(await canViewUserFollow(response.data));
                const spillCount = await api.get<{ total: number }>(apiRoutes.entities.root, {
                        params: {
                            entityType: 'comment,impact,solution',
                            count: true,
                            coauthorId: user.id
                    }
                });
                setPostCount(spillCount.data.total);
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        };
        fetchUser();
    }, [user]);

    const getLinkIcon = (type: string, url: string) => {
        if (!type) return null;
    
        switch (true) {
          case type === 'youtube':
            return (
              <span className={`size-4`}>
                <img className={`size-4`} src={`${IMG_CDN}/icons/youtube_icon.svg`} alt="YouTube" />
              </span>
            );
          case type === 'tiktok':
            return (
                <span className={`size-4`}>
                  <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/tiktok_icon.svg`} alt="TikTok" />
                </span>
            );
          case type === 'instagram':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/instagram_icon.png`} alt="Instagram" />
                </span>
            );
          case type === 'x':
            return  (
                <span className={`size-4`}>
                  <img className={`size-4 rounded-full bg-white`} src={`${IMG_CDN}/icons/x_icon.png`} alt="X" />
                </span>
            );
          case type === 'facebook':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/facebook_icon.svg`} alt="Facebook" />
                </span>
            );
          case type === 'reddit':
            return (
                <span className={`size-4`}>
                  <img className={`size-4`} src={`${IMG_CDN}/icons/reddit_icon.svg`} alt="Reddit" />
                </span>
            );
          case type === 'custom':
            try {
                const linkHostname = new URL(url).hostname;
                if (linkHostname) {
                    return (
                        <span className={`size-4`}>
                            <img 
                                className={`size-4 rounded-sm`} 
                                src={`https://icons.duckduckgo.com/ip2/${linkHostname}.ico`} 
                                alt={`${linkHostname} icon`} 
                                onError={(e) => {
                                    const target = e.currentTarget;
                                    target.onerror = null; // Prevent infinite fallback loop
                                    
                                    // Try fallback services in order
                                    const services = [
                                        `https://www.google.com/s2/favicons?domain_url=${linkHostname}`,
                                        `https://favicon.yandex.net/favicon/${linkHostname}`,
                                        `https://f1.allesedv.com/16/${linkHostname}`,
                                        `${IMG_CDN}/icons/link_icon.svg`
                                    ];
                                    
                                    let currentIndex = 0;
                                    const tryNextService = () => {
                                        if (currentIndex < services.length) {
                                            target.src = services[currentIndex];
                                            currentIndex++;
                                            if (currentIndex < services.length) {
                                                target.onerror = tryNextService;
                                            }
                                        }
                                    };
                                    
                                    tryNextService();
                                }}
                            />
                        </span>
                    );
                }
            } catch (error) {
                console.error("Error parsing URL:", error);
            }
            // Default icon if URL parsing fails
            return (
                <span className={`size-4`}>
                    <img className={`size-4 rounded-sm`} src={`${IMG_CDN}/icons/link_icon.svg`} alt="Link" />
                </span>
            );
          default:    
            return null;
        }
      };

    // Function to get alignment data for a specific statement number
    const getAlignmentData = (teabateId: number) => {
        if (!alignments.profileUser) return null;
                    const alignment = alignments.profileUser.find(a => a.teabate.id === teabateId);
            if (!alignment) return null;
            
            return {
                response: alignment.relation.properties.response,
                importance: alignment.relation.properties.importance,
                note: alignment.relation.rel_description || "",
                privacy: alignment.relation.properties.privacy as PrivacyLevel,
                commentId: alignment.relation.properties.comment_id
            };
        };

    // Function to get the current user's alignment data
    const getCurrentUserAlignmentData = (teabateId: number) => {
        if (!alignments.currentUser) return null;
        const alignment = alignments.currentUser.find(a => a.teabate.id === teabateId);
        if (!alignment) return null;
        
        return {
            response: alignment.relation.properties.response,
            importance: alignment.relation.properties.importance
        };
    };

    const handleRespond = (statementId: number) => {
        if (!awaitingUser && !currentUser) {
            setShowAuthModal(true);
        } else {
            setStatementNumber(statementId);
            setShowAlignmentModal(true);
        }
    };

    // Function to handle alignment statement selection
    const handleAlignmentStatementChange = (statementId: number | string) => {
        if (statementId === "summary") {
            // Navigate to summary - since summary is in a separate carousel content, we scroll to start
            if (carouselApi) {
                carouselApi.scrollTo(0);
            }
            return;
        }
        
        // Convert to number if it's a string
        const numericId = typeof statementId === 'string' ? parseInt(statementId, 10) : statementId;
        
        // Build the same order as in renderAlignmentTiles
        const allStatementsMap = new Map<number, {hasProfileUserResponse: boolean, hasCurrentUserResponse: boolean}>();
        
        // Add profile user's statements
        alignmentStatements.forEach((statement) => {
            const currentUserData = getCurrentUserAlignmentData(statement.id);
            const hasCurrentUserResponse = currentUserData?.response !== null && currentUserData?.response !== undefined;
            allStatementsMap.set(statement.id, {
                hasProfileUserResponse: true,
                hasCurrentUserResponse: hasCurrentUserResponse
            });
        });
        
        // Add current user's statements
        if (alignments.currentUser) {
            alignments.currentUser
                .filter((item: any) => item?.teabate && item.teabate.properties?.dimension && item.teabate.properties.dimension !== 'none')
                .forEach((item: any) => {
                    if (!allStatementsMap.has(item.teabate.id)) {
                        const currentUserData = getCurrentUserAlignmentData(item.teabate.id);
                        const hasCurrentUserResponse = currentUserData?.response !== null && currentUserData?.response !== undefined;
                        allStatementsMap.set(item.teabate.id, {
                            hasProfileUserResponse: false,
                            hasCurrentUserResponse: hasCurrentUserResponse
                        });
                    }
                });
        }
        
        // Get answered and unanswered statements
        const answeredStatements: number[] = [];
        const unansweredStatements: number[] = [];
        
        Array.from(allStatementsMap.entries()).forEach(([id, info]) => {
            const bothHaveStatement = info.hasProfileUserResponse && info.hasCurrentUserResponse;
            if (bothHaveStatement) {
                answeredStatements.push(id);
            } else {
                unansweredStatements.push(id);
            }
        });
        
        // Combine in the same order as the carousel items
        const visibleStatementIds = [...answeredStatements, ...unansweredStatements];
        
        // Find the index in the combined array
        const index = visibleStatementIds.findIndex(id => id === numericId);
        if (index !== -1 && carouselApi) {
            // Navigate directly to the index (no need to add 1 since summary is in separate content)
            carouselApi.scrollTo(index);
        }
    };

    // Function to render alignment tiles for statements with responses
    const renderAlignmentTiles = () => {
        if (isLoadingAlignments) {
            return (
                <div className="flex justify-center items-center p-4">
                    <Loader2 className="size-4 animate-spin" />
                </div>
            );
        }

        if (alignmentsError) {
            return (
                <div className="text-sm text-center py-2">
                    Failed to load perspectives.
                </div>
            );
        }

        // First, collect ALL unique statements from both users
        const allStatementsMap = new Map<number, {id: number, statement: string, hasProfileUserResponse: boolean, hasCurrentUserResponse: boolean}>();
        
        // Add profile user's statements
        alignmentStatements.forEach((statement) => {
            const currentUserData = getCurrentUserAlignmentData(statement.id);
            const hasCurrentUserResponse = currentUserData?.response !== null && currentUserData?.response !== undefined;
            
            allStatementsMap.set(statement.id, {
                id: statement.id,
                statement: statement.statement,
                hasProfileUserResponse: true, // Profile user has this statement
                hasCurrentUserResponse: hasCurrentUserResponse
            });
        });
        
        // Add current user's statements that profile user doesn't have
        if (alignments.currentUser) {
            alignments.currentUser
                .filter((item: any) => item?.teabate && item.teabate.properties?.dimension && item.teabate.properties.dimension !== 'none')
                .forEach((item: any) => {
                    const statementId = item.teabate.id;
                    if (!allStatementsMap.has(statementId)) {
                        const currentUserData = getCurrentUserAlignmentData(statementId);
                        const hasCurrentUserResponse = currentUserData?.response !== null && currentUserData?.response !== undefined;
                        
                        allStatementsMap.set(statementId, {
                            id: statementId,
                            statement: item.teabate.title || "",
                            hasProfileUserResponse: false,
                            hasCurrentUserResponse: hasCurrentUserResponse
                        });
                    }
                });
        }
        
        // Create arrays to hold answered and unanswered items separately
        const answeredItems: React.ReactNode[] = [];
        const unansweredItems: React.ReactNode[] = [];
        
        // Create arrays to store visible statements for the dropdown
        const answeredStatements: Array<{id: number, statement: string, hasResponse: boolean}> = [];
        const unansweredStatements: Array<{id: number, statement: string, hasResponse: boolean}> = [];
        
        // Process all statements
        Array.from(allStatementsMap.values()).forEach((statementInfo) => {
            const statementId = statementInfo.id;
            const alignmentData = statementInfo.hasProfileUserResponse ? getAlignmentData(statementId) : null;
            const currentUserData = getCurrentUserAlignmentData(statementId);
            
            // Consider it "answered" only if BOTH users have responded (when both have the statement)
            // OR if it's a statement only one user has and they've responded
            const bothHaveStatement = statementInfo.hasProfileUserResponse && statementInfo.hasCurrentUserResponse;
            const isAnswered = bothHaveStatement; // Both must have answered for it to be in "Answered"
            
            // Create the statement object for dropdown
            const statementObj = {
                id: statementId,
                statement: statementInfo.statement,
                hasResponse: isAnswered
            };
            
            // Create the carousel item
            const carouselItem = (
                <CarouselItem key={statementId}>
                    <AlignmentTile
                        statement={statementInfo.statement}
                        statementNumber={statementId}
                        response={alignmentData?.response || 0}
                        importance={alignmentData?.importance || 0}
                        targetHasResponse={statementInfo.hasProfileUserResponse}
                        note={alignmentData?.note}
                        commentId={alignmentData?.commentId}
                        privacy={alignmentData?.privacy}
                        currentUserResponse={currentUserData?.response}
                        currentUserImportance={currentUserData?.importance}
                        currentUserViewerRelation={profileUser?.viewer_relation as ViewerRelation}
                        isPanelOpen={isPanelOpen}
                        onPanelOpenChange={handlePanelOpenChange}
                        actorType="user"
                        onRespond={() => handleRespond(statementId)}
                        hasResponded={statementInfo.hasCurrentUserResponse}
                        statements={[]} // Will be filled in later
                        onStatementChange={handleAlignmentStatementChange}
                        showSummaryOption={true}
                        targetActorId={profileUser?.id}
                        targetActorName={profileUser?.username}
                    />
                </CarouselItem>
            );
            
            // Add to the appropriate arrays
            if (isAnswered) {
                answeredStatements.push(statementObj);
                answeredItems.push(carouselItem);
            } else {
                unansweredStatements.push(statementObj);
                unansweredItems.push(carouselItem);
            }
        });
        
        // Combine the arrays in the desired order: answered first, then unanswered
        const visibleStatements = [...answeredStatements, ...unansweredStatements];
        const renderedItems = [...answeredItems, ...unansweredItems];
        
        // Only return the carousel if we have items to display
        if (renderedItems.length === 0) {
            return (
                <div className="text-sm text-center py-2">
                    {isOwnProfile ? "You have" : `${user.username} has`} not shared any perspectives yet.
                </div>
            );
        }
        
        // Create the summary tile
        const summaryTile = (
            <CarouselItem key="summary">
                <AlignmentSummaryDisplay
                    targetUsers={profileUser ? [profileUser] : []}
                    currentUser={currentUser}
                    targetLabel={profileUser?.username || ""}
                    currentUserViewerRelation={profileUser?.viewer_relation as ViewerRelation}
                    onStatementSelect={handleAlignmentStatementChange}
                    isTeam={false}
                />
            </CarouselItem>
        );
        
        // Update the statements prop for each carousel item with the complete list
        const finalRenderedItems = renderedItems.map((item, index) => {
            // Clone the item and update its props
            return React.cloneElement(item as React.ReactElement, {
                key: `statement-${index}`,
                children: React.cloneElement((item as React.ReactElement).props.children as React.ReactElement, {
                    statements: visibleStatements // Always pass all statements for consistency
                })
            });
        });
        
        // Add the summary tile at the beginning
        //const allItems = [summaryTile, ...finalRenderedItems];
        
        return (
            <Carousel 
                className="w-full space-y-2"
                setApi={setCarouselApi}
            >
                <CarouselContent className="max-w-[90vw]">
                    {summaryTile}
                </CarouselContent>
                <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-left font-semibold items-left px-1">
                    <span className="text-eoe-text dark:text-eoe-dark-text">Responses</span>
                </div>
                <CarouselContent className="max-w-[90vw]">
                    {finalRenderedItems}
                </CarouselContent>
                <div className="flex flex-row px-1 justify-between">
                    <CarouselPrevious size="lg" />
                    <CarouselNext size="lg" />
                </div>
            </Carousel>
        );
    };

    // Localized labels for profile section tabs
    const principlesTabLabel = getTaskUiStrings(resolveTaskLocale()).principlesSectionTitle;

    // Define tabs for profile sections
    const profileTabs = [
        { label: 'Background', value: 'background' },
        { label: 'Perspective', value: 'perspective' },
        { label: principlesTabLabel, value: 'principles' },
    ];

    // Handle tab change
    const handleProfileTabChange = (newTab: string) => {
        setSelectedProfileTab(newTab);
    };

    const handlePanelOpenChange = (open: boolean) => {
        setIsPanelOpen(open);
    };

    const handleOpenStory = () => {
        if (!profileUser?.id) {
            return;
        }
        navigate(buildUserStoriesPath(profileUser.id));
    };

    // Render content based on selected tab
    const renderProfileTabContent = () => {
        switch (selectedProfileTab) {
            case 'perspective':
                return (
                    <>
                        {renderAlignmentTiles()}
                    </>
                );
            case 'background':
                return (
                    <UserBackgroundTile aboutDetails={profileUser?.about || {}} />
                );
            case 'principles':
                return profileUser?.id ? (
                    <UserPrinciplesSection userId={profileUser.id} />
                ) : null;
            default:
                return null;
        }
    };

    return (
        <div className="bg-eoe-bg dark:bg-eoe-dark-bg">
            <div className="max-w-5xl mx-auto px-2 py-2">
                {/* Profile Header */}
                <div className="flex flex-col items-left space-y-4">
                    {/* Avatar */}
                    {/*
                     * Username is always a profile link (the consistent
                     * "name click = profile" half of the Instagram contract).
                     * The avatar below uses the standardized `<ActorAvatar>`,
                     * which paints a story ring + opens the viewer when the
                     * user has an active story, and otherwise falls back to
                     * opening the profile.
                     */}
                    {profileUser ? (
                        <ActorName
                            actor={toActorFromUser(profileUser)}
                            label={profileUser.username}
                            className="flex flex-row items-center gap-2 text-xl font-bold text-eoe-text-heading dark:text-eoe-dark-text-heading text-wrap break-all"
                        />
                    ) : null}

                    <div className="flex-row grid grid-cols-3 sm:flex sm:flex-row items-center gap-2 sm:gap-16">
                        <div className="col-span-1">
                            <ActorAvatar
                                actor={toActorFromUser(profileUser)}
                                size={ACTOR_AVATAR_SIZES.profile}
                            />
                        </div>

                        {/* Stats */}
                        <div className="flex flex-row gap-4 justify-between sm:justify-start col-span-2 text-sm">
                            <Link to={`/user/${profileUser?.username}/overview`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {postCount || 0}
                                </span>
                                <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    spills
                                    </span>
                                </div>
                            </Link>
                            <button onClick={() => setShowFollowersModal(canViewFollow)} className={`${canViewFollow ? '' : 'cursor-default'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {followerCount}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        followers
                                    </span>
                                </div>
                            </button>
                            <button onClick={() => setShowFollowingModal(canViewFollow)} className={`${canViewFollow ? '' : 'cursor-default'}`}>
                                <div className="flex flex-col items-center">
                                    <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                                        {followingCount}
                                    </span>
                                    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        following
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Name and Username */}
                    <div className="text-left">
                        {profileUser?.display_name && (
                            <h1 className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                                {profileUser?.display_name}
                            </h1>
                        )}
                        {profileUser?.about?.about_bio_brief?.value && (
                            <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub whitespace-normal">
                                {profileUser?.about?.about_bio_brief?.value}
                            </p>
                        )}
                    </div>

                    {/* Edit Profile/Follow Button */}
                    {isOwnProfile ? (
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <Link
                                to="/settings/profile"
                                className="flex flex-row flex-shrink-0 shadow-sm items-center bg-transparent border-2 px-3 py-1 font-semibold text-eoe-text dark:text-eoe-dark-text border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover rounded-full text-sm w-fit"
                            >
                                <span className="text-eoe-text dark:text-eoe-dark-text">Edit profile</span>
                            </Link>
                            {canPromoteProfile ? (
                                <MenuOrDrawer
                                    open={profileMenuOpen}
                                    onOpenChange={setProfileMenuOpen}
                                    title={storyUi.promoteMenuTitle}
                                    trigger={(
                                        <button
                                            type="button"
                                            aria-label={storyUi.profileMoreActionsLabel}
                                            aria-haspopup="menu"
                                            aria-expanded={profileMenuOpen}
                                            onClick={() => setProfileMenuOpen(true)}
                                            className="flex items-center justify-center h-8 w-8 rounded-full border-2 border-eoe-text dark:border-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text dark:text-eoe-dark-text"
                                        >
                                            <MoreHorizontal className="size-4" aria-hidden="true" />
                                        </button>
                                    )}
                                    items={[
                                        {
                                            id: "promote-profile",
                                            label: storyUi.promoteProfileOnAdBreaks,
                                            icon: Megaphone,
                                            onSelect: () => setPromoteModalOpen(true),
                                        } satisfies MenuOrDrawerItem,
                                    ]}
                                />
                            ) : null}
                            <div
                                className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50/80 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-300"
                                title={awardBudgetTitle}
                                aria-label={awardBudgetTitle}
                            >
                                <Award className="size-3.5" aria-hidden />
                                <span>{storyUi.awardGivingBudgetLabel}</span>
                                <span className="tabular-nums">{awardBudget}</span>
                            </div>
                            <div
                                className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50/80 px-2.5 py-1 text-xs font-semibold text-sky-700 dark:border-sky-800/70 dark:bg-sky-950/40 dark:text-sky-300"
                                title={promotionCreditsTitle}
                                aria-label={promotionCreditsTitle}
                            >
                                <Clapperboard className="size-3.5" aria-hidden />
                                <span>{storyUi.promotionCreditsLabel}</span>
                                <span className="tabular-nums">{promotionCredits}</span>
                            </div>
                            <div
                                className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50/80 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:border-violet-800/70 dark:bg-violet-950/40 dark:text-violet-300"
                                title={reputationTitle}
                                aria-label={reputationTitle}
                            >
                                <Ribbon className="size-3.5" aria-hidden />
                                <span>{storyUi.reputationReceivedLabel}</span>
                                <span className="tabular-nums">{reputationReceived}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <FollowButton
                                entityId={user.id}
                                entityType="user"
                                entityName={user.username}
                                isPrivate={user.properties_public?.is_private}
                                showAllOptions={true}
                                onFollowChange={(isFollowing) => {
                                    // Optionally update follower count or other UI elements
                                    if (profileUser) {
                                        setProfileUser({
                                            ...profileUser,
                                            follower_count: (profileUser.follower_count || 0) + (isFollowing ? 1 : -1)
                                        });
                                    }
                                }}
                            />
                            {!profileIsPrivate && currentUser ? (
                                <ContentAwardButton
                                    entityType="user"
                                    entityId={user.id}
                                    authorUserId={user.id}
                                    targetLabel={user.display_name || user.username || "profile"}
                                />
                            ) : null}
                            <div
                                className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50/80 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:border-violet-800/70 dark:bg-violet-950/40 dark:text-violet-300"
                                title={reputationTitle}
                                aria-label={reputationTitle}
                            >
                                <Ribbon className="size-3.5" aria-hidden />
                                <span>{storyUi.reputationReceivedLabel}</span>
                                <span className="tabular-nums">{reputationReceived}</span>
                            </div>
                        </div>
                    )}
                    {promoteModalOpen ? (
                        <PromoteEntityModal
                            isOpen={promoteModalOpen}
                            onClose={() => setPromoteModalOpen(false)}
                            entityType="user"
                            entityId={user.id}
                            entityLabel={user.display_name || user.username || null}
                            sponsor={{
                                actorType: "user",
                                actorId: user.id,
                                balance: promotionCredits,
                            }}
                        />
                    ) : null}

                    {hasStoryPlacements && profileUser?.id ? (
                        <div className="flex flex-row gap-2">
                            <button
                                type="button"
                                onClick={handleOpenStory}
                                className="flex flex-row flex-shrink-0 shadow-sm items-center bg-transparent border-2 px-3 py-1 font-semibold text-eoe-text dark:text-eoe-dark-text border-eoe-accent dark:border-eoe-accent hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover rounded-full text-sm w-fit"
                            >
                                {storyUi.viewStory}
                            </button>
                        </div>
                    ) : null}

                    {/* Top Issues */}
                    {!isLoadingRankings && issueRankings.length > 0 && (
                        <DetailPanel 
                            title="Prioritea"
                            showTopCollapse={false}
                            showBottomCollapse={true}
                            className="mt-2 bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                            headingContent={
                                (() => {
                                    const issueId = issueRankings[0].issueId as number | undefined;
                                    const s = typeof issueId === 'number' ? profileIssueSentiments[issueId] : undefined;
                                    const rankBase = 'flex-shrink-0 w-6 pb-0.5 pt-3 rounded-full text-white text-xs font-semibold flex flex-col items-center justify-between gap-0 leading-none';
                                    const rankColor = s === 1 ? 'bg-green-600' : s === -1 ? 'bg-red-600' : 'bg-eoe-gray dark:bg-eoe-gray';
                                    const rankClasses = `${rankBase} ${rankColor}`;
                                    return (
                                        <div className="flex p-1 gap-1 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-xl">
                                            {/* Rank Number colored by profile user's sentiment (icon inside) */}
                                            <div className={rankClasses}>
                                                <span>#{issueRankings[0].ranking}</span>
                                                {typeof s === 'number' && s !== 0 && (
                                                    s === 1 ? <Smile className="size-4" /> : <Frown className="size-4" />
                                                )}
                                            </div>
                                            {/* Issue + viewer sentiment control */}
                                            <div className="flex-grow min-w-0">
                                                <EntityAvatar
                                                    entity={issueRankings[0].issue as unknown as Issue}
                                                    entityType="issue"
                                                    className="flex-grow min-w-0"
                                                    entityChip={true}
                                                    showCloseChip={false}
                                                    showLink={true}
                                                />
                                                <EntitySentiment
                                                    entityType="issue"
                                                    entityId={issueId ?? null}
                                                    className="mt-1 flex-shrink-0 px-1"
                                                    variant="bar"
                                                />
                                            </div>
                                        </div>
                                    );
                                })()
                            }
                        >
                            <div className="max-h-[50svh] overflow-y-auto space-y-2">
                                {issueRankings.slice(1).map((ranking) => {
                                    const issueId = ranking.issueId as number | undefined;
                                    const s = typeof issueId === 'number' ? profileIssueSentiments[issueId] : undefined;
                                    const rankBase = 'flex-shrink-0 w-6 pb-0.5 pt-3 rounded-full text-white text-xs font-semibold flex flex-col items-center justify-between gap-0 leading-none';
                                    const rankColor = s === 1 ? 'bg-green-600' : s === -1 ? 'bg-red-600' : 'bg-eoe-gray dark:bg-eoe-gray';
                                    const rankClasses = `${rankBase} ${rankColor}`;

                                    return (
                                        <div key={ranking.id} className="flex gap-1 p-1 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-xl">
                                            {/* Rank Number colored by profile user's sentiment (icon inside) */}
                                            <div className={rankClasses}>
                                                <span>#{ranking.ranking}</span>
                                                {typeof s === 'number' && s !== 0 && (
                                                    s === 1 ? <Smile className="size-4" /> : <Frown className="size-4" />
                                                )}
                                            </div>
                                            {/* Issue + viewer sentiment control */}
                                            <div className="flex-grow min-w-0">
                                                <EntityAvatar
                                                    entity={ranking.issue as unknown as Issue}
                                                    entityType="issue"
                                                    className="flex-grow min-w-0"
                                                    entityChip={true}
                                                    showCloseChip={false}
                                                    showLink={true}
                                                />
                                                <EntitySentiment
                                                    entityType="issue"
                                                    entityId={issueId ?? null}
                                                    className="mt-1 flex-shrink-0 px-1"
                                                    variant="bar"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </DetailPanel>
                    )}
                    
                    {/* About Section */}
                    <DetailPanel 
                        title="About" 
                        className="mt-2 bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                        showTopCollapse={false}
                        showBottomCollapse={true}
                        headingContent={
                            <div className="flex flex-col items-start gap-2">
                                {profileUser?.about?.about_bio_links?.value && (() => {
                                    try {
                                        const links = profileUser.about.about_bio_links.value;
                                        if (links && links.length > 0) {
                                            return (
                                                <div className="flex flex-wrap gap-1">
                                                    {links.map((link: SocialLink, index: number) => (
                                                        <a
                                                            key={index}
                                                            href={link.url}
                                                            target="_blank"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                window.open(link.url, '_blank');
                                                            }}
                                                            rel="noopener noreferrer"
                                                            className="flex flex-row gap-1 items-center btn-secondary bg-slate-500 dark:bg-slate-800 hover:bg-slate-400 dark:hover:bg-slate-700 py-1 text-xs text-eoe-dark-text dark:text-eoe-dark-text-sub"
                                                        >
                                                            {getLinkIcon(link.type, link.url)}
                                                            {link.label}
                                                        </a>
                                                    ))}
                                                </div>
                                            );
                                        }
                                    } catch (error) {
                                        console.error('Error parsing social links:', error);
                                    }
                                    return null;
                                })()}
                            </div>
                        }
                    >
                        {/* Replace the perspective text with tabs */}
                        <div className="mb-2">
                            <ScrollableTabsList
                                tabs={profileTabs}
                                navlink={false}
                                selectedTab={selectedProfileTab}
                                pageTitle="User Profile"
                                onTabChange={handleProfileTabChange}
                                className="text-xs"
                            />
                        </div>
                        
                        {/* Render content based on selected tab */}
                        {renderProfileTabContent()}
                    </DetailPanel>

                    {/* Activitea Section */}
                    <DetailPanel
                        title="Activitea"
                        className="mt-2 bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                        showTopCollapse={false}
                        showBottomCollapse={true}
                    >
                        <UserActivityCarousel userId={user.id} />
                    </DetailPanel>
                </div>
            </div>

            {/* Followers Modal */}
            {showFollowersModal && (
                <FollowersModal
                    entityId={user.id}
                    entityType="user"
                    viewType="followers"
                    onClose={() => setShowFollowersModal(false)}
                />
            )}
            {showFollowingModal && (
                <FollowersModal
                    entityId={user.id}
                    entityType="user"
                    viewType="following"
                    onClose={() => setShowFollowingModal(false)}
                />
            )}
            {showAlignmentModal && (
                <UserAlignmentModal
                    onClose={() => setShowAlignmentModal(false)}
                    initialStatementId={statementNumber}
                />
            )}
        </div>
    );
};

export default UserProfilePlate; 